import {
  Advertisement,
  CreateAdvertisementPayload,
  UpdateAdvertisementPayload,
} from '../types/advertisement';
import { ApiErrorResponse } from '../types/auth';
import { PaginationParams, PaginatedResponse, buildQueryString } from '../types/pagination';
import { ADVERTISEMENT_ENDPOINTS } from '../utils/endPoints';
import { createApiClient } from '../utils/axiosConfig';
import axios from 'axios';

export type { Advertisement, CreateAdvertisementPayload, UpdateAdvertisementPayload } from '../types/advertisement';
export { AdvertisementType } from '../types/advertisement';

const apiClient = createApiClient();

export const getAllAdvertisements = async (params?: PaginationParams): Promise<PaginatedResponse<Advertisement>> => {
  try {
    const qs = buildQueryString(params ?? {});
    const response = await apiClient.get<{ data: Advertisement[]; total: number; page: number; limit: number; totalPages: number }>(
      `${ADVERTISEMENT_ENDPOINTS.GET_ALL}${qs}`
    );
    const d = response.data;
    return {
      items: d.data ?? [],
      total: d.total ?? 0,
      page: d.page ?? 1,
      limit: d.limit ?? 20,
      totalPages: d.totalPages ?? 0,
    };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to fetch advertisements');
    }
    console.error('Get advertisements error:', error);
    throw new Error('An unexpected error occurred while fetching advertisements.');
  }
};

export const getAdvertisement = async (id: number): Promise<Advertisement> => {
  try {
    const response = await apiClient.get<Advertisement>(ADVERTISEMENT_ENDPOINTS.GET(id));
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to fetch advertisement');
    }
    console.error('Get advertisement error:', error);
    throw new Error('An unexpected error occurred while fetching advertisement.');
  }
};

export const createAdvertisement = async (advertisementData: CreateAdvertisementPayload & { imageFile?: File }): Promise<Advertisement> => {
  try {
    let dataToSend: any = advertisementData;
    let headers: any = {};

    if (advertisementData.imageFile instanceof File) {
      const formData = new FormData();
      Object.entries(advertisementData).forEach(([key, value]) => {
        if (key !== 'imageFile' && value !== undefined && value !== null) {
          formData.append(key, value as any);
        }
      });
      formData.append('image', advertisementData.imageFile);
      dataToSend = formData;
      headers['Content-Type'] = 'multipart/form-data';
    }

    const response = await apiClient.post<Advertisement>(ADVERTISEMENT_ENDPOINTS.CREATE, dataToSend, { headers });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to create advertisement');
    } else {
      console.error('Create advertisement service error:', error);
      throw new Error('An unexpected error occurred while creating the advertisement.');
    }
  }
};

export const updateAdvertisement = async (id: number, advertisementData: UpdateAdvertisementPayload & { imageFile?: File }): Promise<Advertisement> => {
  try {
    let dataToSend: any = advertisementData;
    let headers: any = {};

    if (advertisementData.imageFile instanceof File) {
      const formData = new FormData();
      Object.entries(advertisementData).forEach(([key, value]) => {
        if (key !== 'imageFile' && value !== undefined && value !== null) {
          formData.append(key, value as any);
        }
      });
      formData.append('image', advertisementData.imageFile);
      dataToSend = formData;
      headers['Content-Type'] = 'multipart/form-data';
    }

    const response = await apiClient.put<Advertisement>(ADVERTISEMENT_ENDPOINTS.UPDATE(id), dataToSend, { headers });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to update advertisement');
    } else {
      console.error('Update advertisement service error:', error);
      throw new Error('An unexpected error occurred while updating the advertisement.');
    }
  }
};

export const deleteAdvertisement = async (id: number): Promise<{ message: string }> => {
  try {
    const response = await apiClient.delete<{ message: string }>(ADVERTISEMENT_ENDPOINTS.DELETE(id));
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to delete advertisement');
    } else {
      console.error('Delete advertisement service error:', error);
      throw new Error('An unexpected error occurred while deleting the advertisement.');
    }
  }
};
